import { z } from 'zod'
import { pollImportIndex, pollImportShow, pollImportPreview } from './poll-import'
import { historyReviewIndex } from './history-review'
import { memberHistoryShow, memberHistoryEvent, memberHistoryPoll } from './member-history'

import { parseCalendarDate } from '../lib/dates'
import { defineResponseContract, responseValue } from './response-contract'

const nonblankString = () => responseValue.string({ blank: 'forbid' })
const errorMessages = () => responseValue.array(nonblankString(), { blank: 'allow' })
const optionalError = () => errorMessages().optional()
const calendarDate = () => z.union([
  z.date(),
  z.iso.date().transform(parseCalendarDate),
])

const formErrors = <TShape extends z.ZodRawShape>(shape: TShape) =>
  responseValue.object({ base: optionalError(), ...shape }, { blank: 'allow' })

const memberSchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
  meetupName: nonblankString().nullable(),
  meetupId: z.number().int().nullable(),
}, { blank: 'forbid' })

const userSchema = responseValue.object({
  id: z.number().int().positive(),
  email: nonblankString(),
  status: nonblankString(),
  role: z.enum(['admin', 'member']),
  memberId: z.number().int().positive().nullable(),
  memberName: nonblankString().nullable(),
  loginLocked: z.boolean(),
}, { blank: 'forbid' })

const pollLinkRequests = z.array(z.strictObject({
  id: z.number().int().positive(), pollId: z.number().int().positive(), pollTitle: nonblankString(),
  accountId: z.number().int().positive(), email: nonblankString(), votingName: nonblankString(),
  memberName: nonblankString().nullable(), submittedAt: nonblankString(),
}))

const publicDirectorSchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
}, { blank: 'forbid' })

const filmSchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
  year: z.number().int(),
  alternateName: nonblankString().nullable(),
  directorNames: responseValue.array(nonblankString(), { blank: 'allow' }),
  fullTitle: nonblankString(),
  imdbUrl: nonblankString().nullable(),
  votingImageUrl: nonblankString().nullable(),
  imageUrl: nonblankString().nullable(),
}, { blank: 'forbid' })

const publicFilmSummarySchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
  year: z.number().int(),
  alternateName: nonblankString().nullable(),
  imageUrl: nonblankString().nullable(),
  directors: responseValue.array(publicDirectorSchema, { blank: 'forbid' }),
}, { blank: 'forbid' })

const venueSchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
}, { blank: 'forbid' })

const filmSelectionSchema = responseValue.object({
  id: z.number().int().positive(),
  filmSelectionType: nonblankString(),
  filmId: z.number().int().positive(),
  memberId: z.number().int().positive().nullable(),
}, { blank: 'forbid' })

const filmPollSchema = responseValue.object({
  id: z.number().int().positive(),
  topic: nonblankString(),
  memberId: z.number().int().positive().nullable(),
}, { blank: 'forbid' })

const adminPollSchema = responseValue.object({
  workflowStage: nonblankString(), reviewComplete: z.boolean(), calculating: z.boolean(), correctionChangesEvent: z.boolean(),
  votingSlug: z.string().regex(/^[0-9A-F]{8}$/),
  id: z.number().int().positive(),
  topic: nonblankString(),
  year: z.number().int().nullable(),
  state: nonblankString(),
  memberId: z.number().int().positive().nullable(),
  responsibleOrganiserId: z.number().int().positive().nullable(),
  nominationCount: z.number().int().nonnegative(),
  ready: z.boolean(),
  closingLocal: nonblankString().nullable(),
  closingOffset: nonblankString().nullable(),
  closedAt: nonblankString().nullable(),
  cancelledFromState: nonblankString().nullable(),
  cancellationReason: nonblankString().nullable(),
  replacementPollId: z.number().int().positive().nullable(),
  replacesPollId: z.number().int().positive().nullable(),
  published: z.boolean(),
  currentResultVersionId: z.number().int().positive().nullable(),
  publishedResultVersionId: z.number().int().positive().nullable(),
  publicationNote: nonblankString().nullable(),
  selectionCount: z.number().int().nonnegative(),
}, { blank: 'forbid' })

const adminNominationSchema = responseValue.object({
  id: z.number().int().positive(),
  filmTitle: nonblankString(),
  memberName: nonblankString(),
  trailerUrl: nonblankString().nullable(),
  reuseOverrideReason: nonblankString().nullable(),
}, { blank: 'forbid' })

const eventSchema = responseValue.object({
  id: z.number().int().positive(),
  date: calendarDate(),
  eventType: nonblankString(),
  filmSelectionType: nonblankString().nullable(),
  venueId: z.number().int().positive().nullable(),
  devil: nonblankString().nullable(),
  quote: nonblankString().nullable(),
  meetupUrl: nonblankString().nullable(),
  cancelled: z.boolean(),
  filmSelection: filmSelectionSchema.nullable(),
  filmPoll: filmPollSchema.nullable(),
  filmTitle: nonblankString().nullable(),
}, { blank: 'forbid' })

const publicEventSchema = responseValue.object({
  id: z.number().int().positive(),
  date: calendarDate(),
  eventType: nonblankString(),
  filmSelectionType: nonblankString(),
  cancelled: z.boolean(),
  upcoming: z.boolean(),
  devil: nonblankString().nullable(),
  quote: nonblankString().nullable(),
  meetupUrl: nonblankString().nullable(),
  film: publicFilmSummarySchema.nullable(),
  venueName: nonblankString().nullable(),
  pollTopic: nonblankString().nullable(),
  movedOnline: z.boolean(),
}, { blank: 'forbid' })

const publicFilmSchema = responseValue.object({
  id: z.number().int().positive(),
  name: nonblankString(),
  year: z.number().int(),
  alternateName: nonblankString().nullable(),
  imageUrl: nonblankString().nullable(),
  directors: responseValue.array(publicDirectorSchema, { blank: 'forbid' }),
  selectable: z.boolean(),
  nextSelectableDate: calendarDate().nullable(),
  events: responseValue.array(publicEventSchema, { blank: 'allow' }),
}, { blank: 'forbid' })

const sharedResponseFields = {
  errors: z.record(z.string(), errorMessages()),
  user: responseValue.object({
    id: z.number().int().positive(),
    name: nonblankString(),
  }, { blank: 'forbid' }).nullable(),
}

export const sharedResponsePropsSchema = responseValue.object({
  ...sharedResponseFields,
  csrf: nonblankString(),
}, { blank: 'forbid' })

// Informational pages and their error view have no forms or session token.
export const sessionlessResponsePropsSchema = responseValue.object({
  ...sharedResponseFields,
  csrf: nonblankString().nullable(),
}, { blank: 'forbid' })

export const flashResponseContracts = {
  notice: nonblankString(),
  alert: nonblankString(),
} as const

const title = nonblankString()
const baseErrors = errorMessages()
const attendanceEventSchema = z.strictObject({
  id: z.number().int().positive(), date: z.iso.date(), title: nonblankString(), category: nonblankString(), categoryId: z.number().int().positive(), filmDiscussion: z.boolean(), venueId: z.number().int().positive().nullable(),
  format: z.enum(['online', 'in_person', 'in_person_moved_online']), cancelled: z.boolean(),
  status: z.enum(['tracked', 'partially_tracked', 'empty']), meetupEventId: z.string().nullable(),
})
const attendanceRowSchema = z.strictObject({ id: z.number().int().positive(), memberId: z.number().int().positive(),
  name: nonblankString(), source: z.enum(['website_scrape', 'manual_addition']), state: z.enum(['accepted', 'removed', 'combined']) })
const attendancePagination = z.strictObject({ page: z.number().int().positive(), total: z.number().int().nonnegative(), perPage: z.number().int().positive() })
const attendanceSuggestion = z.strictObject({ key: nonblankString(), id: z.number().int().positive(), label: nonblankString() })
const attendanceImportSummary = z.strictObject({ id: z.number().int().positive(), title: nonblankString(), date: z.iso.date().nullable(), eventId: z.number().int().positive().nullable(), status: nonblankString() })
const attendanceHistory = z.array(z.strictObject({ id: z.number().int().positive(), action: nonblankString(), reason: z.string().nullable(), actor: nonblankString(), at: nonblankString() }))

const attendanceContext = z.strictObject({ token: nonblankString(),
  categories: z.array(z.strictObject({ id: z.number().int().positive(), name: nonblankString(), filmDiscussion: z.boolean() })),
  venues: z.array(z.strictObject({ id: z.number().int().positive(), name: nonblankString() })),
})
const comparisonObservation = z.strictObject({ label: nonblankString(), entryId: nonblankString(), profileState: nonblankString(),
  memberId: z.number().int().positive().nullable(), memberName: z.string().nullable(), matchMethod: z.string().nullable(), excluded: z.boolean(), reason: z.string().nullable() })

const noFieldErrors = responseValue.object({}, { blank: 'allow' })

export const pageResponseNames = [
  'About',
  'CodeOfConduct',
  'WhatToExpect',
  'directors/Show',
  'events/Index',
  'film_polls/Show',
  'film_polls/Results',
  'film_polls/LinkRequest',
  'film_polls/Claim',
  'film_polls/ClaimRecovery',
  'Admin',
  'Error',
  'RollbarIndex',
  'RollbarTest',
  'auth/Login',
  'auth/CreateAccount',
  'auth/SignupConfirmation',
  'auth/ResetPassword',
  'auth/ResetPasswordRequest',
  'auth/SmsAuth',
  'auth/SmsRequest',
  'auth/VerifyAccount',
  'auth/VerifyAccountResend',
  'admin/film_poll_imports/Index',
  'admin/needs_review/Index',
  'admin/member_history/Show',
  'admin/member_history/Event',
  'admin/member_history/Poll',
  'admin/film_poll_imports/Show',
  'admin/film_poll_imports/Preview',
  'admin/attendance/Index',
  'admin/attendance/Show',
  'admin/attendance/Import',
  'admin/attendance/Tracking',
  'admin/attendance/Comparison',
  'admin/events/Form',
  'admin/events/Index',
  'admin/film_polls/History',
  'admin/film_polls/Submission',
  'admin/film_polls/Form',
  'admin/film_polls/Review',
  'admin/film_polls/Index',
  'admin/directors/Form',
  'admin/directors/Index',
  'admin/films/Form',
  'admin/films/Index',
  'admin/members/Form',
  'admin/members/Index',
  'admin/members/Merge',
  'admin/members/RecoverLogin',
  'admin/members/CorrectMerge',
  'admin/users/Form',
  'admin/users/Index',
  'admin/users/Merge',
] as const

export type PageResponseName = typeof pageResponseNames[number]

export const pageResponseContracts = {
  CodeOfConduct: defineResponseContract({
    name: 'page.CodeOfConduct',
    schema: responseValue.object({}, { blank: 'allow' }),
  }),
  WhatToExpect: defineResponseContract({
    name: 'page.WhatToExpect',
    schema: responseValue.object({}, { blank: 'allow' }),
  }),
  'admin/film_poll_imports/Index': defineResponseContract({ name: 'page.admin.film_poll_imports.Index', schema: pollImportIndex }),
  'admin/member_history/Show': defineResponseContract({ name: 'page.admin.member_history.Show', schema: memberHistoryShow }),
  'admin/member_history/Event': defineResponseContract({ name: 'page.admin.member_history.Event', schema: memberHistoryEvent }),
  'admin/member_history/Poll': defineResponseContract({ name: 'page.admin.member_history.Poll', schema: memberHistoryPoll }),
  'admin/needs_review/Index': defineResponseContract({ name: 'page.admin.needs_review.Index', schema: historyReviewIndex }),
  'admin/film_poll_imports/Show': defineResponseContract({ name: 'page.admin.film_poll_imports.Show', schema: pollImportShow }),
  'admin/film_poll_imports/Preview': defineResponseContract({ name: 'page.admin.film_poll_imports.Preview', schema: pollImportPreview }),
  'film_polls/Claim': defineResponseContract({
    name: 'page.film_polls.Claim',
    schema: responseValue.object({
      pollTopic: nonblankString(), claimPath: nonblankString(), alreadyClaimed: z.boolean(), conflict: z.boolean(),
    }, { blank: 'forbid' }),
  }),
  'film_polls/ClaimRecovery': defineResponseContract({
    name: 'page.film_polls.ClaimRecovery',
    schema: responseValue.object({ pollId: responseValue.string({ blank: 'allow' }) }, { blank: 'forbid' }),
  }),
  'film_polls/Show': defineResponseContract({
    name: 'page.film_polls.Show',
    schema: responseValue.object({
      poll: responseValue.object({
        id: z.number().int().positive(), votingSlug: z.string().regex(/^[0-9A-F]{8}$/), topic: nonblankString(), year: z.number().int(),
        state: nonblankString(), closedAt: nonblankString().nullable(), closingAt: nonblankString().nullable(), acceptingVotes: z.boolean(),
        votingPeriod: z.number().int().nonnegative(), replacementPollId: z.number().int().positive().nullable(), replacementPollSlug: z.string().regex(/^[0-9A-F]{8}$/).nullable(),
      }, { blank: 'forbid' }),
      nominations: responseValue.array(responseValue.object({
        id: z.number().int().positive(), title: nonblankString(), year: z.number().int(),
        directors: responseValue.array(nonblankString(), { blank: 'allow' }), trailerUrl: nonblankString().nullable(),
        imageUrl: nonblankString().nullable(), imdbUrl: nonblankString().nullable(),
      }, { blank: 'allow' }), { blank: 'allow' }),
      accountEmail: nonblankString().nullable(),
      meetupName: nonblankString().nullable(),
      draftSessionId: nonblankString(),
      accepted: responseValue.object({
        number: z.number().int().positive(), name: nonblankString(), email: nonblankString().nullable(),
        submittedAt: nonblankString(),
        ranking: responseValue.array(z.number().int().positive(), { blank: 'forbid' }),
      }, { blank: 'forbid' }).nullable(),
    }, { blank: 'forbid' }),
    safeValuePaths: ['poll.id', 'nominations.*.id'],
  }),
  'film_polls/LinkRequest': defineResponseContract({
    name: 'page.film_polls.LinkRequest',
    schema: z.strictObject({
      poll: z.strictObject({ topic: nonblankString(), year: z.number().int().nullable(), votingSlug: z.string().regex(/^[0-9A-F]{8}$/) }),
      accountEmail: nonblankString(),
      linkRequest: z.strictObject({ votingName: nonblankString(), notified: z.boolean(), resolved: z.boolean(), rejected: z.boolean() }).nullable(),
    }),
    safeValuePaths: [],
  }),
  'film_polls/Results': defineResponseContract({
    name: 'page.film_polls.Results',
    schema: responseValue.object({
      poll: responseValue.object({
        id: z.number().int().positive(), topic: nonblankString(), year: z.number().int(),
        state: nonblankString(), cancelled: z.boolean(), cancellationReason: nonblankString().nullable(),
        replacementPollId: z.number().int().positive().nullable(), replacementPollSlug: z.string().regex(/^[0-9A-F]{8}$/).nullable(), underReview: z.boolean(),
        closedAt: nonblankString().nullable(), votingSlug: z.string().regex(/^[0-9A-F]{8}$/), publishedAt: nonblankString().nullable(), publicationNote: nonblankString().nullable(),
      }, { blank: 'forbid' }),
      preview: z.boolean(),
      accountEmail: nonblankString().nullable(),
      linkRequestPending: z.boolean(),
      result: responseValue.object({
        evidenceMode: z.enum(['live_ballots', 'historical_ballots', 'historical_aggregate']),
        version: z.number().int().positive(), winnerId: z.number().int().positive(), voteCount: z.number().int().positive(),
        weights: responseValue.array(responseValue.object({ rank: z.number().int().positive(), value: z.number().finite() }, { blank: 'forbid' }), { blank: 'forbid' }),
        decidedBy: nonblankString(), manualExplanation: nonblankString().nullable(),
        rows: responseValue.array(responseValue.object({
          nominationId: z.number().int().positive(), place: z.number().int().positive(),
          score: z.number().finite(), rankCounts: responseValue.array(z.number().int().nonnegative(), { blank: 'forbid' }),
        }, { blank: 'forbid' }), { blank: 'forbid' }),
      }, { blank: 'forbid' }).nullable(),
      nominations: responseValue.array(responseValue.object({
        id: z.number().int().positive(), title: nonblankString(), year: z.number().int(),
        directors: responseValue.array(nonblankString(), { blank: 'allow' }),
      }, { blank: 'forbid' }), { blank: 'allow' }),
      contributions: responseValue.array(responseValue.object({
        revisionId: z.number().int().positive(), ranking: responseValue.array(responseValue.object({
          nominationId: z.number().int().positive(), rank: z.number().int().positive(), points: z.number().finite(),
        }, { blank: 'forbid' }), { blank: 'forbid' }),
      }, { blank: 'forbid' }), { blank: 'allow' }),
      ownSelections: responseValue.array(responseValue.object({
        revisionId: z.number().int().positive().nullable(), included: z.boolean(),
        ranking: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
      }, { blank: 'forbid' }), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['poll.id', 'nominations.*.id', 'result.rows.*.nominationId'],
  }),
  'admin/film_polls/Index': defineResponseContract({
    name: 'page.admin.film_polls.Index',
    schema: responseValue.object({ polls: responseValue.array(adminPollSchema, { blank: 'allow' }) }, { blank: 'forbid' }),
    safeValuePaths: ['polls.*.id'],
  }),
  'admin/film_polls/History': defineResponseContract({
    name: 'page.admin.film_polls.History',
    schema: z.object({
      poll: z.object({ id: z.number().int().positive(), title: nonblankString() }),
      events: z.array(z.object({ id: z.number().int().positive(), date: nonblankString() })),
      entries: z.array(z.object({
        id: nonblankString(), at: nonblankString(), title: nonblankString(), actor: nonblankString().nullable(),
        details: z.array(z.object({ label: nonblankString(), value: z.string() })), selectionId: z.number().int().positive().nullable(),
      })),
    }),
    safeValuePaths: ['poll.id'],
  }),
  'admin/film_polls/Submission': defineResponseContract({
    name: 'page.admin.film_polls.Submission',
    schema: z.object({
      poll: z.object({ id: z.number().int().positive(), title: nonblankString() }),
      submission: z.object({
        id: z.number().int().positive(), name: nonblankString(), submittedAt: nonblankString(), revision: z.number().int().positive(),
        rankings: z.array(z.object({ rank: z.number().int().positive(), title: nonblankString() })),
      }),
    }),
    safeValuePaths: ['poll.id', 'submission.id'],
  }),
  'admin/film_polls/Form': defineResponseContract({
    name: 'page.admin.film_polls.Form',
    schema: responseValue.object({
      linkRequests: pollLinkRequests,
      poll: adminPollSchema.nullable(),
      votingUrl: z.string().url().nullable(),
      submissions: z.array(z.object({ id: z.number().int().positive(), name: nonblankString(), submittedAt: nonblankString() })),
      liveToken: nonblankString().nullable(),
      pendingEvents: z.array(z.object({ id: z.number().int().positive(), label: nonblankString(), pollId: z.number().int().positive() })),
      nominations: responseValue.array(adminNominationSchema, { blank: 'allow' }),
      members: responseValue.array(responseValue.object({ id: z.number().int().positive(), name: nonblankString(), meetupName: nonblankString().nullable(), disabled: z.boolean() }, { blank: 'forbid' }), { blank: 'allow' }),
      films: responseValue.array(responseValue.object({ id: z.number().int().positive(), title: nonblankString(), selectable: z.boolean(), nextSelectableDate: nonblankString().nullable(), lastEventId: z.number().int().positive().nullable(), lastEventDate: nonblankString().nullable(), directors: responseValue.array(nonblankString(), { blank: 'allow' }) }, { blank: 'forbid' }), { blank: 'allow' }),
      organisers: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      directors: responseValue.array(responseValue.object({ id: z.number().int().positive(), name: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      createdDirectorId: z.number().int().positive().nullable(),
      createdFilmId: z.number().int().positive().nullable(),
      createdMemberId: z.number().int().positive().nullable(),
      attempts: responseValue.array(responseValue.object({ id: z.number().int().positive(), status: nonblankString(), failureCode: nonblankString().nullable(), createdAt: nonblankString(), tiedNominationIds: responseValue.array(z.number().int().positive(), { blank: 'allow' }) }, { blank: 'forbid' }), { blank: 'allow' }),
      notifications: responseValue.array(responseValue.object({
        id: z.number().int().positive(), kind: nonblankString(), status: nonblankString(),
        failureCode: nonblankString().nullable(), attempts: z.number().int().nonnegative(),
        sentAt: nonblankString().nullable(), localDate: nonblankString().nullable(), recipientEmail: nonblankString().nullable(),
      }, { blank: 'forbid' }), { blank: 'allow' }),
      historical: z.boolean(), historicalImportId: z.number().int().positive().nullable(),
      openingAvailable: z.boolean(),
    }, { blank: 'forbid' }),
    safeValuePaths: ['poll.id', 'nominations.*.id', 'members.*.id', 'films.*.id', 'organisers.*.id', 'attempts.*.id', 'notifications.*.id'],
  }),
  'admin/film_polls/Review': defineResponseContract({
    name: 'page.admin.film_polls.Review',
    schema: responseValue.object({
      linkRequests: pollLinkRequests,
      poll: responseValue.object({ id: z.number().int().positive(), title: nonblankString(), state: nonblankString(), reviewComplete: z.boolean() }, { blank: 'forbid' }),
      liveToken: nonblankString(),
      members: responseValue.array(responseValue.object({
        id: z.number().int().positive(), name: nonblankString(), meetupName: nonblankString().nullable(),
        disabled: z.boolean(), email: nonblankString().nullable(), accountId: z.number().int().positive().nullable(),
      }, { blank: 'forbid' }), { blank: 'allow' }),
      selections: responseValue.array(responseValue.object({
        id: z.number().int().positive(), enteredName: nonblankString(), enteredEmail: nonblankString().nullable(),
        createdDuringReview: z.boolean(), verifiedEmail: z.boolean(), submittedAt: nonblankString(), revisionId: z.number().int().positive(),
        rankings: responseValue.array(nonblankString(), { blank: 'allow' }),
        signedInMemberId: z.number().int().positive().nullable(), accountId: z.number().int().positive().nullable(),
        review: responseValue.object({
          decision: nonblankString(), memberId: z.number().int().positive().nullable(), proposalKey: nonblankString().nullable(),
          proposedAdminName: nonblankString().nullable(), proposedMeetupName: nonblankString().nullable(),
          reason: nonblankString().nullable(), version: z.number().int().nonnegative(), stale: z.boolean(),
        }, { blank: 'forbid' }).nullable(),
        suggestions: responseValue.array(responseValue.object({
          memberId: z.number().int().positive(), reasons: responseValue.array(nonblankString(), { blank: 'forbid' }),
          disabled: z.boolean(),
        }, { blank: 'forbid' }), { blank: 'allow' }),
      }, { blank: 'forbid' }), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['poll.id', 'members.*.id', 'selections.*.id'],
  }),
  'admin/members/Merge': defineResponseContract({
    name: 'page.admin.members.Merge',
    schema: responseValue.object({
      retired: responseValue.object({ id: z.number().int().positive(), name: nonblankString(),
        meetupName: nonblankString().nullable(), meetupId: z.number().int().nullable(),
        disabled: z.boolean(), accountId: z.number().int().positive().nullable() }, { blank: 'forbid' }),
      candidates: responseValue.array(responseValue.object({ id: z.number().int().positive(), name: nonblankString(),
        meetupName: nonblankString().nullable(), meetupId: z.number().int().nullable(),
        disabled: z.boolean(), accountId: z.number().int().positive().nullable() }, { blank: 'forbid' }), { blank: 'allow' }),
      survivor: responseValue.object({ id: z.number().int().positive(), name: nonblankString(),
        meetupName: nonblankString().nullable(), meetupId: z.number().int().nullable(),
        disabled: z.boolean(), accountId: z.number().int().positive().nullable() }, { blank: 'forbid' }).nullable(),
      preview: responseValue.object({
        token: nonblankString(),
        attendanceCount: z.number().int().nonnegative(), sourceEntryCount: z.number().int().nonnegative(),
        openConflicts: responseValue.array(responseValue.object({
          pollId: z.number().int().positive(), selectionIds: responseValue.array(z.number().int().positive(), { blank: 'forbid' }),
          names: responseValue.array(nonblankString(), { blank: 'forbid' }),
        }, { blank: 'forbid' }), { blank: 'allow' }),
        nominationConflicts: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
        affectedPollIds: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
        accounts: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString(),
          role: nonblankString(), status: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
        organiserPolls: responseValue.array(z.tuple([z.number().int().positive(), nonblankString()]), { blank: 'allow' }),
      }, { blank: 'forbid' }).nullable(),
      organisers: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      returnTo: nonblankString().nullable(),
    }, { blank: 'forbid' }),
    safeValuePaths: ['retired.id', 'survivor.id', 'candidates.*.id', 'preview.affectedPollIds.*'],
  }),
  'admin/members/RecoverLogin': defineResponseContract({
    name: 'page.admin.members.RecoverLogin',
    schema: responseValue.object({
      member: responseValue.object({ id: z.number().int().positive(), name: nonblankString() }, { blank: 'forbid' }),
      currentAccount: responseValue.object({ id: z.number().int().positive(), email: nonblankString(),
        role: z.enum(['admin', 'member']), status: nonblankString() }, { blank: 'forbid' }).nullable(),
      token: nonblankString(),
      organiserPolls: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
      organisers: responseValue.array(responseValue.object({ id: z.number().int().positive(),
        email: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['member.id', 'currentAccount.id', 'organiserPolls.*'],
  }),
  About: defineResponseContract({
    name: 'page.About',
    schema: responseValue.object({}, { blank: 'allow' }),
  }),
  'directors/Show': defineResponseContract({
    name: 'page.directors.Show',
    schema: responseValue.object({
      director: publicDirectorSchema,
      films: responseValue.array(publicFilmSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['director.id', 'films.*.id', 'films.*.directors.*.id', 'films.*.events.*.id'],
  }),
  'events/Index': defineResponseContract({
    name: 'page.events.Index',
    schema: responseValue.object({
      selectedYear: z.number().int(),
      availableYears: responseValue.array(z.number().int(), { blank: 'forbid' }),
      query: responseValue.string({ blank: 'allow' }),
      searchType: z.enum(['all', 'film', 'director']),
      searchCounts: responseValue.object({
        all: z.number().int().nonnegative(),
        film: z.number().int().nonnegative(),
        director: z.number().int().nonnegative(),
      }, { blank: 'forbid' }),
      searchFilterOptions: responseValue.array(z.enum(['all', 'film', 'director']), { blank: 'allow' }),
      events: responseValue.array(publicEventSchema, { blank: 'allow' }),
      upcomingEvents: responseValue.array(publicEventSchema, { blank: 'allow' }),
      films: responseValue.array(publicFilmSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['events.*.id', 'upcomingEvents.*.id', 'films.*.id', 'films.*.events.*.id'],
  }),
  Admin: defineResponseContract({
    name: 'page.Admin',
    schema: responseValue.object({
      rollbarTestsPath: nonblankString(),
      summary: responseValue.object({
        members: z.number().int().nonnegative(),
        films: z.number().int().nonnegative(),
        upcomingEvents: z.number().int().nonnegative(),
        openPolls: z.number().int().nonnegative(),
        needsReview: z.number().int().nonnegative(),
        needsAttendance: z.number().int().nonnegative(),
      }, { blank: 'forbid' }),
    }, { blank: 'forbid' }),
  }),
  Error: defineResponseContract({
    name: 'page.Error',
    schema: responseValue.object({ message: nonblankString() }, { blank: 'forbid' }),
  }),
  RollbarIndex: defineResponseContract({
    name: 'page.RollbarIndex',
    schema: responseValue.object({}, { blank: 'allow' }),
  }),
  RollbarTest: defineResponseContract({
    name: 'page.RollbarTest',
    schema: responseValue.object({
      backend_error_path: nonblankString(),
      response_contract_diagnostics: z.boolean(),
    }, { blank: 'forbid' }),
  }),
  'auth/Login': defineResponseContract({
    name: 'page.auth.Login',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ email: optionalError(), password: optionalError() }),
      emailValue: responseValue.string({ blank: 'allow' }),
      registrationAvailable: z.boolean(),
      pollResultsPath: nonblankString().nullable(),
    }, { blank: 'forbid' }),
  }),
  'auth/CreateAccount': defineResponseContract({
    name: 'page.auth.CreateAccount',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ email: optionalError(), voting_name: optionalError() }),
      emailValue: responseValue.string({ blank: 'allow' }),
      pollTitle: nonblankString().nullable(),
      votingNameValue: responseValue.string({ blank: 'allow' }),
    }, { blank: 'forbid' }),
  }),
  'auth/SignupConfirmation': defineResponseContract({
    name: 'page.auth.SignupConfirmation',
    schema: z.strictObject({ pollPath: z.string().regex(/^\/film_polls\/[0-9A-F]{8}$/) }),
  }),
  'auth/ResetPassword': defineResponseContract({
    name: 'page.auth.ResetPassword',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ password: optionalError() }),
    }, { blank: 'forbid' }),
  }),
  'auth/ResetPasswordRequest': defineResponseContract({
    name: 'page.auth.ResetPasswordRequest',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: noFieldErrors,
      emailValue: responseValue.string({ blank: 'allow' }),
    }, { blank: 'forbid' }),
  }),
  'auth/SmsAuth': defineResponseContract({
    name: 'page.auth.SmsAuth',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ 'sms-code': optionalError() }),
    }, { blank: 'forbid' }),
  }),
  'auth/SmsRequest': defineResponseContract({
    name: 'page.auth.SmsRequest',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: noFieldErrors,
    }, { blank: 'forbid' }),
  }),
  'auth/VerifyAccount': defineResponseContract({
    name: 'page.auth.VerifyAccount',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ password: optionalError() }),
    }, { blank: 'forbid' }),
  }),
  'auth/VerifyAccountResend': defineResponseContract({
    name: 'page.auth.VerifyAccountResend',
    schema: responseValue.object({
      title,
      baseErrors,
      fieldErrors: formErrors({ email: optionalError() }),
      emailValue: responseValue.string({ blank: 'allow' }),
    }, { blank: 'forbid' }),
  }),
  'admin/attendance/Index': defineResponseContract({ name: 'page.admin.attendance.Index', schema: z.strictObject({
    events: z.array(attendanceEventSchema.extend({ count: z.number().int().nonnegative() })), imports: z.array(attendanceImportSummary),
    pagination: attendancePagination, importPagination: attendancePagination, years: z.array(z.string()), filters: z.strictObject({ year: z.string(), status: z.string(), memberId: z.string(), cancelled: z.boolean() }),
  }) }),
  'admin/attendance/Show': defineResponseContract({ name: 'page.admin.attendance.Show', schema: z.strictObject({
    event: attendanceEventSchema, context: attendanceContext,
    tracking: z.strictObject({ acceptedCount: z.number().int().nonnegative(), pending: z.number().int().nonnegative(), importId: z.number().int().positive().nullable(), requiresManualComplete: z.boolean() }), attendance: z.array(attendanceRowSchema), imports: z.array(attendanceImportSummary), pagination: attendancePagination, history: attendanceHistory,
    pendingAttendance: z.array(z.strictObject({ id: z.number().int().positive(), label: z.string(), memberName: z.string().nullable(), reviewPage: z.number().int().positive() })), pendingPagination: attendancePagination,
  }) }),
  'admin/attendance/Import': defineResponseContract({ name: 'page.admin.attendance.Import', schema: z.strictObject({
    draft: z.strictObject({ id: z.number().int().positive(), captureId: nonblankString(), title: nonblankString(), date: z.iso.date().nullable(),
      status: nonblankString(), capturedAt: nonblankString(), sourceCancelled: z.boolean(), sourceFormat: z.string().nullable(), event: attendanceEventSchema.nullable(), pending: z.number().int().nonnegative() }),
    entries: z.array(z.strictObject({ id: z.number().int().positive(), label: z.string().nullable(), entryId: nonblankString(), meetupId: z.string().nullable(),
      memberId: z.number().int().positive().nullable(), memberName: z.string().nullable(), canRememberName: z.boolean(), matchMethod: z.string().nullable(), excluded: z.boolean(), reason: z.string().nullable(), attendance: attendanceRowSchema.nullable() })),
    suggestions: z.strictObject({ events: z.array(attendanceSuggestion), films: z.array(attendanceSuggestion), venues: z.array(attendanceSuggestion), members: z.record(z.string(), z.array(attendanceSuggestion)) }),
    pagination: attendancePagination, history: attendanceHistory, context: attendanceContext.nullable(),
    comparison: z.strictObject({ previousCaptureId: z.string().nullable(), added: z.number().int().nonnegative(), omitted: z.number().int().nonnegative(), changed: z.number().int().nonnegative(), unidentified: z.number().int().nonnegative(), previousImportId: z.number().int().positive().nullable(), eventChanges: z.array(z.strictObject({ field: z.string(), before: z.string().nullable(), after: z.string().nullable() })) }),
    categories: z.array(z.strictObject({ id: z.number().int().positive(), name: nonblankString(), filmDiscussion: z.boolean() })),
    venues: z.array(z.strictObject({ id: z.number().int().positive(), name: nonblankString() })),
  }) }),
  'admin/attendance/Tracking': defineResponseContract({ name: 'page.admin.attendance.Tracking', schema: z.strictObject({
    token: nonblankString(), events: z.array(z.strictObject({ id: z.number().int().positive(), title: nonblankString(), date: z.iso.date(),
      count: z.number().int().nonnegative(), sourceStatus: z.string().nullable(), pending: z.number().int().nonnegative(), blockers: z.array(z.string()), requiresManualCompletion: z.boolean() })),
  }) }),
  'admin/attendance/Comparison': defineResponseContract({ name: 'page.admin.attendance.Comparison', schema: z.strictObject({
    draftId: z.number().int().positive(), title: nonblankString(), eventId: z.number().int().positive().nullable(), previousImportId: z.number().int().positive().nullable(),
    sourceStatus: nonblankString(), previousSourceStatus: z.string().nullable(), filter: nonblankString(), pagination: attendancePagination,
    rows: z.array(z.strictObject({ kind: z.enum(['added', 'omitted', 'changed', 'unchanged', 'unidentified', 'recorded_only']), meetupId: z.string().nullable(),
      before: comparisonObservation.nullable(), after: comparisonObservation.nullable(), records: z.array(attendanceRowSchema) })),
  }) }),
  'admin/events/Form': defineResponseContract({
    name: 'page.admin.events.Form',
    schema: responseValue.object({
      event: eventSchema.nullable(),
      eventCategoryId: z.number().int().positive(),
      categories: z.array(z.strictObject({ id: z.number().int().positive(), name: nonblankString(), filmDiscussion: z.boolean() })),
      films: responseValue.array(filmSchema, { blank: 'allow' }),
      members: responseValue.array(memberSchema, { blank: 'allow' }),
      venues: responseValue.array(venueSchema, { blank: 'allow' }),
      filmSelectionTypes: responseValue.array(nonblankString(), { blank: 'forbid' }),
      eventTypes: responseValue.array(nonblankString(), { blank: 'forbid' }),
      defaultVenueId: z.number().int().positive().nullable(),
      completedPolls: responseValue.array(responseValue.object({
        id: z.number().int().positive(), title: nonblankString(), topic: nonblankString(), state: nonblankString(),
        memberId: z.number().int().positive().nullable(), memberName: nonblankString().nullable(),
        winnerFilmId: z.number().int().positive().nullable(), winnerTitle: nonblankString().nullable(), nominatorName: nonblankString().nullable(),
      }, { blank: 'forbid' }), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['event.id', 'films.*.id', 'members.*.id', 'venues.*.id', 'completedPolls.*.id'],
  }),
  'admin/events/Index': defineResponseContract({
    name: 'page.admin.events.Index',
    schema: responseValue.object({
      events: responseValue.array(eventSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['events.*.id'],
  }),
  'admin/directors/Form': defineResponseContract({
    name: 'page.admin.directors.Form',
    schema: responseValue.object({ director: publicDirectorSchema }, { blank: 'forbid' }),
    safeValuePaths: ['director.id'],
  }),
  'admin/directors/Index': defineResponseContract({
    name: 'page.admin.directors.Index',
    schema: responseValue.object({
      directors: responseValue.array(responseValue.object({
        id: z.number().int().positive(), name: nonblankString(), filmCount: z.number().int().nonnegative(),
      }, { blank: 'forbid' }), { blank: 'allow' }),
      search: responseValue.string({ blank: 'allow' }),
      pagination: responseValue.object({
        page: z.number().int().positive(), perPage: z.number().int().positive(),
        total: z.number().int().nonnegative(), totalPages: z.number().int().positive(),
      }, { blank: 'forbid' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['directors.*.id'],
  }),
  'admin/films/Form': defineResponseContract({
    name: 'page.admin.films.Form',
    schema: responseValue.object({ film: filmSchema.nullable() }, { blank: 'forbid' }),
    safeValuePaths: ['film.id'],
  }),
  'admin/films/Index': defineResponseContract({
    name: 'page.admin.films.Index',
    schema: responseValue.object({
      films: responseValue.array(filmSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['films.*.id'],
  }),
  'admin/members/Form': defineResponseContract({
    name: 'page.admin.members.Form',
    schema: responseValue.object({ member: memberSchema.nullable() }, { blank: 'forbid' }),
    safeValuePaths: ['member.id'],
  }),
  'admin/members/Index': defineResponseContract({
    name: 'page.admin.members.Index',
    schema: responseValue.object({
      members: responseValue.array(memberSchema, { blank: 'allow' }),
      mergedMemberIds: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['members.*.id'],
  }),
  'admin/members/CorrectMerge': defineResponseContract({
    name: 'page.admin.members.CorrectMerge',
    schema: responseValue.object({
      retired: responseValue.object({ id: z.number().int().positive(), name: nonblankString(), meetupName: nonblankString().nullable(), meetupId: z.number().int().nullable(), disabled: z.boolean(), accountId: z.number().int().positive().nullable() }, { blank: 'forbid' }),
      source: responseValue.object({ id: z.number().int().positive(), name: nonblankString(), meetupName: nonblankString().nullable(), meetupId: z.number().int().nullable(), disabled: z.boolean(), accountId: z.number().int().positive().nullable() }, { blank: 'forbid' }),
      token: nonblankString(),
      suggestedSelectionIds: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
      selections: responseValue.array(responseValue.object({ id: z.number().int().positive(), pollId: z.number().int().positive(), name: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      nominations: responseValue.array(responseValue.object({ id: z.number().int().positive(), pollId: z.number().int().positive() }, { blank: 'forbid' }), { blank: 'allow' }),
      eventSelections: responseValue.array(responseValue.object({ id: z.number().int().positive(), filmId: z.number().int().positive() }, { blank: 'forbid' }), { blank: 'allow' }),
      topicPolls: responseValue.array(responseValue.object({ id: z.number().int().positive(), topic: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      accounts: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString(), status: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      attendance: z.array(z.strictObject({ id: z.number().int().positive(), eventId: z.number().int().positive(), source: z.string(), removed: z.boolean() })),
      attendanceSourceEntries: z.array(z.strictObject({ id: z.number().int().positive(), importId: z.number().int().positive(), entryId: z.string(), attendanceId: z.number().int().positive().nullable() })),
      targets: responseValue.array(responseValue.object({ id: z.number().int().positive(), name: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['retired.id', 'source.id', 'selections.*.id', 'nominations.*.id', 'eventSelections.*.id', 'topicPolls.*.id'],
  }),
  'admin/users/Form': defineResponseContract({
    name: 'page.admin.users.Form',
    schema: responseValue.object({
      linkRequests: pollLinkRequests,
      account: userSchema.nullable(),
      members: responseValue.array(memberSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['account.id', 'members.*.id'],
  }),
  'admin/users/Index': defineResponseContract({
    name: 'page.admin.users.Index',
    schema: responseValue.object({
      linkRequests: pollLinkRequests,
      users: responseValue.array(userSchema, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    safeValuePaths: ['users.*.id'],
  }),
  'admin/users/Merge': defineResponseContract({
    name: 'page.admin.users.Merge',
    schema: responseValue.object({
      retired: responseValue.object({ id: z.number().int().positive(), email: nonblankString(),
        status: nonblankString(), role: nonblankString(), memberId: z.number().int().positive().nullable() }, { blank: 'forbid' }),
      survivor: responseValue.object({ id: z.number().int().positive(), email: nonblankString(),
        status: nonblankString(), role: nonblankString(), memberId: z.number().int().positive().nullable() }, { blank: 'forbid' }).nullable(),
      candidates: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString(),
        status: nonblankString(), role: nonblankString(), memberId: z.number().int().positive().nullable() }, { blank: 'forbid' }), { blank: 'allow' }),
      preview: responseValue.object({ token: nonblankString(),
        openConflicts: responseValue.array(responseValue.object({ pollId: z.number().int().positive(),
          selectionIds: responseValue.array(z.number().int().positive(), { blank: 'forbid' }),
          names: responseValue.array(nonblankString(), { blank: 'forbid' }) }, { blank: 'forbid' }), { blank: 'allow' }),
        affectedPollIds: responseValue.array(z.number().int().positive(), { blank: 'allow' }),
        organiserPolls: responseValue.array(z.tuple([z.number().int().positive(), nonblankString()]), { blank: 'allow' }),
      }, { blank: 'forbid' }).nullable(),
      organisers: responseValue.array(responseValue.object({ id: z.number().int().positive(), email: nonblankString() }, { blank: 'forbid' }), { blank: 'allow' }),
      returnTo: nonblankString().nullable(),
    }, { blank: 'forbid' }),
    safeValuePaths: ['retired.id', 'survivor.id', 'candidates.*.id', 'preview.affectedPollIds.*'],
  }),
} satisfies Record<PageResponseName, ReturnType<typeof defineResponseContract>>

const adminMutation = <TShape extends z.ZodRawShape>(shape: TShape) => ({
  kind: 'page',
  success: responseValue.object({
    flash: responseValue.object({ notice: nonblankString() }, { blank: 'forbid' }),
  }, { blank: 'forbid' }),
  failure: responseValue.object({
    errors: formErrors(shape),
  }, { blank: 'forbid' }),
} as const)

const adminDestroy = ({ alert = false }: { alert?: boolean } = {}) => ({
  kind: 'page',
  success: responseValue.object({
    flash: alert
      ? z.union([
          responseValue.object({ notice: nonblankString() }, { blank: 'forbid' }),
          responseValue.object({ alert: nonblankString() }, { blank: 'forbid' }),
        ])
      : responseValue.object({ notice: nonblankString() }, { blank: 'forbid' }),
  }, { blank: 'forbid' }),
  failure: responseValue.object({
    errors: formErrors({}),
  }, { blank: 'forbid' }),
} as const)

const adminEventMutation = adminMutation({
  meetup_url: optionalError(),
  event_category: optionalError(),
  cancelled: optionalError(),
  date: optionalError(),
  event_type: optionalError(),
  film: optionalError(),
  film_poll: optionalError(),
  film_selection_type: optionalError(),
  member: optionalError(),
  topic: optionalError(),
  venue: optionalError(),
})
const adminFilmMutation = adminMutation({
  imdb_url: optionalError(),
  voting_image: optionalError(),
  director_names: optionalError(),
  image: optionalError(),
  name: optionalError(),
  year: optionalError(),
})
const adminMemberMutation = adminMutation({
  meetup_id: optionalError(),
  meetup_name: optionalError(),
  name: optionalError(),
})
const adminUserMutation = adminMutation({
  email: optionalError(),
  member_id: optionalError(),
  status: optionalError(),
})

export const operationResponseContracts = {
  filmPollLinkRequestCreate: adminMutation({ voting_name: optionalError() }),
  adminPollImportFilmCreate: adminFilmMutation,
  adminPollImportSave: adminMutation({ base: optionalError() }),
  adminAttendanceUpload: adminMutation({ base: optionalError() }),
  adminAttendanceSave: adminMutation({ base: optionalError() }),
  filmPollVote: adminMutation({ base: optionalError() }),
  adminFilmPollCreate: adminMutation({ topic: optionalError(), member: optionalError(), base: optionalError() }),
  adminFilmPollUpdate: adminMutation({ topic: optionalError(), member: optionalError(), base: optionalError() }),
  adminFilmPollSettingsUpdate: adminMutation({ responsible_organiser_id: optionalError(), closing_local: optionalError() }),
  adminFilmPollFilmCreate: adminFilmMutation,
  adminFilmPollDirectorCreate: adminMutation({ name: optionalError() }),
  adminFilmPollMemberCreate: adminMutation({ name: optionalError(), meetup_name: optionalError() }),
  adminFilmNominationCreate: adminMutation({ film: optionalError(), member: optionalError(), base: optionalError() }),
  adminFilmNominationDestroy: adminDestroy({ alert: true }),
  adminFilmPollPrepare: adminDestroy({ alert: true }),
  adminFilmPollClose: adminDestroy({ alert: true }),
  adminFilmPollReopen: adminMutation({ base: optionalError() }),
  adminFilmPollReturnToDraft: adminMutation({ base: optionalError() }),
  adminFilmPollCancel: adminMutation({ base: optionalError() }),
  adminFilmPollRestore: adminMutation({ base: optionalError() }),
  adminFilmPollReplace: adminMutation({ base: optionalError() }),
  adminFilmPollTrailerUpdate: adminMutation({ base: optionalError() }),
  adminFilmPollNotificationRetry: adminDestroy({ alert: true }),
  adminEventCreate: adminEventMutation,
  adminEventUpdate: adminEventMutation,
  adminEventDestroy: adminDestroy(),
  adminDirectorUpdate: adminMutation({ name: optionalError() }),
  adminDirectorDestroy: adminDestroy({ alert: true }),
  adminFilmCreate: adminFilmMutation,
  adminFilmUpdate: adminFilmMutation,
  adminFilmDestroy: adminDestroy({ alert: true }),
  adminMemberCreate: adminMemberMutation,
  adminMemberUpdate: adminMemberMutation,
  adminMemberDestroy: adminDestroy({ alert: true }),
  adminUserCreate: adminUserMutation,
  adminUserUpdate: adminUserMutation,
  adminUserDestroy: adminDestroy(),
  adminUserUnlock: adminDestroy(),
  rollbarMalformedForm: {
    kind: 'page',
    success: responseValue.object({
      flash: responseValue.object({}, { blank: 'allow' }),
    }, { blank: 'forbid' }),
    failure: responseValue.object({
      errors: formErrors({}),
    }, { blank: 'forbid' }),
  },
  diagnosticNoContent: {
    kind: 'no-content',
  },
} as const

export type OperationResponseName = keyof typeof operationResponseContracts

export function operationResponseKind(operation: string | undefined): 'page' | 'no-content' | undefined {
  if (!operation || !Object.hasOwn(operationResponseContracts, operation)) return
  return operationResponseContracts[operation as keyof typeof operationResponseContracts].kind
}
